// src/components/Toast.tsx
import React, { useEffect } from 'react';
import { CheckCircleIcon, XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface ToastProps {
  message: string;
  type: 'success' | 'error' | 'warning';
  onClose: () => void;
  duration?: number;
}

const Toast: React.FC<ToastProps> = ({ message, type, onClose, duration = 4000 }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const icons = {
    success: <CheckCircleIcon className="h-6 w-6 text-green-400" />,
    error: <XMarkIcon className="h-6 w-6 text-red-400" />,
    warning: <ExclamationTriangleIcon className="h-6 w-6 text-yellow-400" />
  };

  const styles = {
    success: 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200',
    error: 'bg-gradient-to-r from-red-50 to-pink-50 border-red-200',
    warning: 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200'
  };

  const textStyles = {
    success: 'text-green-800',
    error: 'text-red-800',
    warning: 'text-yellow-800'
  };

  return (
    <div className="fixed top-20 right-6 z-50 animate-slide-in">
      <div className={`${styles[type]} rounded-xl shadow-2xl border p-4 flex items-center space-x-3 min-w-[300px] max-w-md backdrop-blur-sm`}>
        <div className="flex-shrink-0">
          {icons[type]}
        </div>
        <div className="flex-1">
          <p className={`text-sm font-medium ${textStyles[type]}`}>
            {message}
          </p>
        </div>
        <button
          onClick={onClose}
          className="flex-shrink-0 ml-4 inline-flex text-gray-400 hover:text-gray-600 transition-colors"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export default Toast;
